import { Container, Graphics, Rectangle, type FederatedPointerEvent } from 'pixi.js';
import { COLORS, BORDER } from '../app/theme';
import { hapticSelection } from '../platform/telegram';

export interface SliderOpts {
  width: number;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange?: (value: number) => void;
}

/** A draggable horizontal slider used for the bet amount on the create-game screen. */
export class Slider extends Container {
  private readonly trackW: number;
  private min: number;
  private max: number;
  private readonly step: number;
  private _value: number;
  private readonly onChangeCb?: (value: number) => void;

  private readonly handle = new Graphics();
  private readonly h = 80;
  private dragging = false;
  private lastHapticAt = 0;

  constructor(opts: SliderOpts) {
    super();
    this.trackW = opts.width;
    this.min = opts.min;
    this.max = Math.max(opts.min, opts.max);
    this.step = Math.max(1, opts.step);
    this.onChangeCb = opts.onChange;
    this._value = this.clamp(opts.value);

    const track = new Graphics()
      .roundRect(0, this.h / 2 - 7, this.trackW, 14, 7)
      .fill({ color: COLORS.sliderTrack })
      .stroke({ width: 1, color: COLORS.buttonTextDark, alpha: 0.3 });
    this.addChild(track, this.handle);

    this.eventMode = 'static';
    this.cursor = 'pointer';
    this.hitArea = new Rectangle(-18, 0, this.trackW + 36, this.h);
    this.on('pointerdown', (e: FederatedPointerEvent) => {
      this.dragging = true;
      this.updateFromEvent(e);
    });
    this.on('globalpointermove', (e: FederatedPointerEvent) => {
      if (this.dragging) this.updateFromEvent(e);
    });
    this.on('pointerup', () => (this.dragging = false));
    this.on('pointerupoutside', () => (this.dragging = false));

    this.redraw();
  }

  private clamp(v: number): number {
    const snapped = Math.round(v / this.step) * this.step;
    return Math.min(this.max, Math.max(this.min, snapped));
  }

  private updateFromEvent(e: FederatedPointerEvent): void {
    const x = e.getLocalPosition(this).x;
    const t = Math.min(1, Math.max(0, x / this.trackW));
    this.setValue(this.min + t * (this.max - this.min));
  }

  private redraw(): void {
    const t = this.max > this.min ? (this._value - this.min) / (this.max - this.min) : 0;
    const x = t * this.trackW;
    this.handle.clear();
    this.handle
      .circle(x, this.h / 2, 26)
      .fill({ color: COLORS.accent })
      .stroke({ width: BORDER.thick, color: COLORS.buttonTextDark });
  }

  setValue(v: number, fire = true): void {
    const nv = this.clamp(v);
    if (nv === this._value) return;
    this._value = nv;
    this.redraw();
    if (fire) {
      const now = performance.now();
      if (now - this.lastHapticAt > 45) {
        this.lastHapticAt = now;
        hapticSelection();
      }
      this.onChangeCb?.(nv);
    }
  }

  /**
   * Re-bounds the track in place (used by create-game when the game-count dropdown changes the
   * per-count min price). Re-clamps the current value to the new range and redraws; fires onChange
   * only if the clamped value actually moved.
   */
  setRange(min: number, max: number, fire = true): void {
    this.min = min;
    this.max = Math.max(min, max);
    const nv = this.clamp(this._value);
    const changed = nv !== this._value;
    this._value = nv;
    this.redraw();
    if (changed && fire) this.onChangeCb?.(nv);
  }

  get value(): number {
    return this._value;
  }
}
